import React, { useEffect, useState, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import scrollToTop from '../helpers/scrollToTop'
import bannerImage from '../images/banner-image.jpg'
import ServiceModal from '../components/ServiceModal'
import ServiceGallery from '../components/ServiceGallery'
import { IconPanel, ServiceIcon } from '../components/ServiceIcon'
import { servicesData } from '../data/services'
import { getServiceMedia } from '../utils/serviceMedia'
import {
  getLengthPrices,
  getPriceSummary,
  groupServices,
  hasLengthPrices,
} from '../utils/servicePrice'

import { slugify } from '../utils/slugify'

// Dados de um serviço tal como o cartão e o modal os mostram.
const toCardData = (svc) => ({
  title: svc.name,
  priceSummary: getPriceSummary(svc),
  lengthPrices: hasLengthPrices(svc) ? getLengthPrices(svc) : null,
  duration: svc.duration ? `${svc.duration} min` : null,
  image: svc.service_image ? `/${svc.service_image}` : null,
  media: getServiceMedia(svc),
  icon: svc.icon || null,
  details: Array.isArray(svc.description)
    ? svc.description
    : svc.description
    ? [svc.description]
    : [],
  group: svc.group || null,
  rawId: svc.service_id,
})

// Props comuns aos dois tipos de cartão: todo o cartão abre o modal, com rato
// ou teclado.
const propsClicaveis = (svc, abrir) => ({
  id: `service-${svc.rawId}`,
  role: 'button',
  tabIndex: 0,
  'aria-label': `Ver detalhes de ${svc.title}`,
  onClick: () => abrir(svc),
  onKeyDown: (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      abrir(svc)
    }
  },
})

const BASE_CARTAO =
  'group bg-white rounded-2xl border border-stone-200/60 shadow-sm hover:shadow-md hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#a5bf99] transition-all duration-300 cursor-pointer'

const Preco = ({ resumo, tamanho = 'text-sm sm:text-base' }) => (
  <span
    className={
      resumo.numeric
        ? `${tamanho} font-semibold text-[#c0a080] tabular-nums`
        : 'text-xs sm:text-sm italic font-light text-[#5c7160]/60'
    }
  >
    {resumo.value}
  </span>
)

// Cartão com fotografia (ou, sem foto própria, o painel com o ícone).
const CartaoFoto = ({ svc, abrir }) => (
  <div {...propsClicaveis(svc, abrir)} className={`flex flex-col overflow-hidden ${BASE_CARTAO}`}>
    {svc.media.length ? (
      <ServiceGallery media={svc.media} title={svc.title} variant="card" />
    ) : (
      <IconPanel name={svc.icon} className="aspect-[4/5]" />
    )}

    {/* O detalhe do serviço vive no modal: no cartão fica só o que
        se lê de relance, senão a grelha vira um muro de texto. */}
    <div className="flex flex-col grow gap-2 p-3 sm:p-4">
      <h3 className="text-sm sm:text-base font-light text-[#5c7160] leading-snug line-clamp-2">
        {svc.title}
      </h3>

      <div className="mt-auto flex items-end justify-between gap-2">
        <span className="text-xs text-[#5c7160]/60">{svc.duration}</span>
        <span className="text-right leading-tight">
          {svc.priceSummary.prefix && (
            <span className="block text-[10px] uppercase tracking-wider text-[#5c7160]/60">
              {svc.priceSummary.prefix}
            </span>
          )}
          <Preco resumo={svc.priceSummary} />
        </span>
      </div>
    </div>
  </div>
)

// Cartão compacto para serviços sem fotografia (cortes, produtos): ícone num
// medalhão, nome e preço. Ocupa uma fração do espaço de um cartão com foto.
const CartaoCompacto = ({ svc, abrir }) => (
  <div {...propsClicaveis(svc, abrir)} className={`flex items-center gap-3 sm:gap-4 p-3 sm:p-4 ${BASE_CARTAO}`}>
    <span className="flex-shrink-0 flex items-center justify-center w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-[#a5bf99]/20 text-[#5c7160] group-hover:bg-[#5c7160] group-hover:text-white transition-colors duration-300">
      <ServiceIcon name={svc.icon} className="h-5 w-5 sm:h-6 sm:w-6" />
    </span>
    <span className="min-w-0 flex-1">
      <span className="block text-sm sm:text-base font-light text-[#5c7160] leading-snug">
        {svc.title}
      </span>
      <span className="block mt-0.5 leading-tight">
        {svc.priceSummary.prefix && (
          <span className="mr-1 text-[11px] text-[#5c7160]/60">{svc.priceSummary.prefix}</span>
        )}
        <Preco resumo={svc.priceSummary} tamanho="text-sm" />
        {svc.duration && (
          <span className="ml-2 text-xs text-[#5c7160]/60">· {svc.duration}</span>
        )}
      </span>
    </span>
  </div>
)

// NEW: find a service by id OR by name
const findServiceFromParam = (services, serviceParam) => {
  if (!serviceParam) return null
  const decoded = decodeURIComponent(serviceParam)
  // try by numeric id first
  let svc = services.find(s => String(s.service_id) === decoded)
  if (svc) return svc
  // fallback by name (case-insensitive)
  return services.find(s => s.name.toLowerCase() === decoded.toLowerCase())
}

function Services() {
  const [searchParams, setSearchParams] = useSearchParams()
  const categoryFromUrl = searchParams.get('category')
  const serviceFromUrl = searchParams.get('service')

  const [services, setServices] = useState([])
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [activeCategory, setActiveCategory] = useState('')
  const [selectedService, setSelectedService] = useState(null)

  const scrollContainerRef = useRef(null)
  const servicesSectionRef = useRef(null)

  // Load the local service catalog instead of calling the backend API.
  useEffect(() => {
    try {
      setLoading(true)
      setServices(servicesData)
      const uniqueCats = [...new Set(servicesData.map(s => s.category))]
      const formatted = uniqueCats.map(cat => ({
        id: slugify(cat),
        name: cat
      }))
      setCategories(formatted)
      setError(null)
    } catch (e) {
      console.error(e)
      setError('Erro ao carregar serviços.')
    } finally {
      setLoading(false)
    }
  }, [])

  // Set initial category after categories load
  useEffect(() => {
    if (loading || !categories.length) return

    // If a specific service is requested, prefer its category
    const svc = findServiceFromParam(services, serviceFromUrl)
    if (svc) {
      const svcCatId = slugify(svc.category)
      setActiveCategory(svcCatId)
      return
    }

    // Otherwise use categoryFromUrl or first category
    const categoriaPedida = categoryFromUrl && slugify(categoryFromUrl)
    if (categoriaPedida && categories.find(c => c.id === categoriaPedida)) {
      setActiveCategory(categoriaPedida)
    } else {
      setActiveCategory(categories[0].id)
    }
  }, [loading, categories, services, categoryFromUrl, serviceFromUrl])

  // Handle service highlighting and modal opening
  useEffect(() => {
    if (loading) return
    
    const svc = findServiceFromParam(services, serviceFromUrl)
    if (!svc) return
    
    // Only scroll to top when opening a specific service
    scrollToTop()

    // ensure the right category is selected
    const svcCatId = slugify(svc.category)
    if (activeCategory !== svcCatId) {
      setActiveCategory(svcCatId)
    }

    // open the modal with details
    setSelectedService(toCardData(svc))

    // scroll & highlight by numeric id
    setTimeout(() => {
      const el = document.getElementById(`service-${svc.service_id}`)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        el.classList.add('highlight-service')
        setTimeout(() => el.classList.remove('highlight-service'), 2000)
      }
    }, 300)
  }, [loading, services, serviceFromUrl, activeCategory])

  const handleCategoryClick = (catId, e) => {
    e.preventDefault()
    
    // Preserve current scroll position
    const currentScrollY = window.pageYOffset
    
    // Update active category immediately for UI responsiveness
    setActiveCategory(catId)
    
    // Update URL parameters - remove service param when changing category
    const newParams = new URLSearchParams()
    newParams.set('category', catId)
    setSearchParams(newParams)
    
    // Restore scroll position after URL update
    setTimeout(() => {
      window.scrollTo(0, currentScrollY)
    }, 0)
    
    // Center the clicked button
    const cont = scrollContainerRef.current
    const btn = e.currentTarget
    if (cont && btn) {
      cont.scrollTo({ 
        left: btn.offsetLeft - cont.clientWidth/2 + btn.clientWidth/2, 
        behavior: 'smooth' 
      })
    }
    
    // Scroll to services section
    if (servicesSectionRef.current) {
      setTimeout(() => {
        const yOff = -80
        const top = servicesSectionRef.current.getBoundingClientRect().top + window.pageYOffset + yOff
        window.scrollTo({ top, behavior: 'smooth' })
      }, 100)
    }
  }

  const openServiceModal = svc => setSelectedService(svc)
  const closeServiceModal = () => setSelectedService(null)

  // retorna lista de serviços da categoria, com image path local
  const getServicesByCategory = catId => {
    if (!catId || !services.length) return []
    
    return services
      .filter(s => slugify(s.category) === catId)
      .map(toCardData)
  }

  // Categorias com grupos (ex.: Cabeleireiro) mostram um título por grupo; um
  // grupo em que nenhum serviço tem fotografia usa os cartões compactos.
  const grupos = groupServices(getServicesByCategory(activeCategory)).map((g) => ({
    ...g,
    compacto: g.itens.every((svc) => !svc.media.length),
  }))

  return (
    <div className="min-h-screen bg-[#F5F1E9]">
      {/* Hero */}
      <div className="relative border-b-2 border-[#F5F1E9]">
        <img src={bannerImage} alt="Serviços" className="object-cover h-[22vh] min-h-[140px] sm:h-[26vh] md:h-[30vh] w-full" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#c0a080]/40 to-[#c0a080]/70" />
        <div className="absolute inset-0 flex items-center justify-center">
          <h2 className="text-[#F5F1E9] text-center px-6 text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-light text-balance">Nossos Serviços</h2>
        </div>
        <div className="absolute bottom-0 left-0 w-full h-16">
          <svg viewBox="0 0 1200 60" className="w-full h-full" preserveAspectRatio="none">
            <path d="M0,60 V30 C150,50 350,40 500,35 C650,30 750,25 900,30 C1050,35 1200,45 1200,45 V60 H0 Z" fill="#F5F1E9"/>
          </svg>
        </div>
      </div>

      {/* Categories */}
      <section className="pt-2 max-w-7xl justify-center mx-auto">
        <div className="relative overflow-hidden container mx-auto hide-scrollbar">
          <div ref={scrollContainerRef} className="flex overflow-x-auto space-x-4 px-6 py-2 hide-scrollbar">
            {loading
              ? Array(6).fill().map((_, i) => (<div key={i} className="w-24 h-8 bg-gray-200 rounded-full animate-pulse" />))
              : categories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={e => handleCategoryClick(cat.id, e)}
                    className={`flex-shrink-0 px-4 py-2 rounded-full transition-all ${
                      activeCategory === cat.id 
                        ? 'bg-[#5c7160] text-white shadow-md' 
                        : 'bg-white text-[#5c7160] hover:bg-[#a5bf99]/20'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
          </div>
        </div>
      </section>

      {/* Service Cards */}
      <section ref={servicesSectionRef} className="py-12 px-6 bg-[#F5F1E9]/5">
        <div className="container mx-auto max-w-6xl">
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
              {Array(8).fill().map((_, i) => (
                <div key={i} className="bg-white rounded-2xl border border-stone-200/60 shadow-sm overflow-hidden">
                  <div className="aspect-[4/5] bg-[#e6e0d3] animate-pulse" />
                  <div className="p-3 sm:p-4 space-y-2">
                    <div className="h-4 bg-[#e6e0d3] rounded animate-pulse" />
                    <div className="h-3 w-16 bg-[#e6e0d3] rounded animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="flex flex-col items-center py-10">
              <p className="text-red-500 mb-4">{error}</p>
              <button onClick={() => window.location.reload()} className="px-4 py-2 bg-[#5c7160] text-white rounded-lg">Tentar novamente</button>
            </div>
          ) : (
            <div className="space-y-12">
              {grupos.map(({ nome, itens, compacto }) => (
                <div key={`${activeCategory}-${nome || '_'}`}>
                  {nome && grupos.length > 1 && (
                    <div className="flex items-center gap-4 mb-5">
                      <h3 className="text-xs sm:text-sm font-medium uppercase tracking-[0.16em] text-[#5c7160]/80">
                        {nome}
                      </h3>
                      <span className="flex-1 h-px bg-gradient-to-r from-[#c0a080]/50 to-transparent" />
                    </div>
                  )}

                  {compacto ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                      {itens.map((svc) => (
                        <CartaoCompacto key={svc.rawId} svc={svc} abrir={openServiceModal} />
                      ))}
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
                      {itens.map((svc) => (
                        <CartaoFoto key={svc.rawId} svc={svc} abrir={openServiceModal} />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {selectedService && <ServiceModal service={selectedService} onClose={closeServiceModal} />}
    </div>
  )
}

export default Services;
